// webscm 2026 10 25 / ODOO-4334 (шаг 1): «кто ссылается на X» — X это id ПЛЮС модель. Числовой id сам по себе объект не называет:
// на Demo7 «6» есть у 138 моделей (товар #6, склад #6, пользователь #6 …), и панель на «партнёр #9» показывала 177 227 ссылок вместо 3 161.
// Владелец: «чтобы я мог в поиске найти где на товар с id=6 идет ссылка и я не нашел бы где на склад 6-й ссылается».
// Чистые функции решения (без стора и DOM) — их проверяет tests/unit/refsTarget.test.mjs; Search.vue / mainStore только вызывают их.

// GUID модели не требует: он уникален сам по себе (бэкенд ограничение моделью для него и не применяет). Тот же вид, что у обхода UUID
// на бэкенде (search.REFS_UUID_RE)
export const REFS_GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function refs_isGuid(id) {
    return REFS_GUID_RE.test(String(id ?? '').trim())
}

// id, который бэкенд прочтёт из чипсов (search.parse_CHIPS: титулы включённых чипсов CHIP-Search через пробел)
export function refs_searchValue(CHIPS) {
    return (CHIPS || [])
        .filter(chip => chip && chip.segment === 'CHIP-Search' && (chip.meta?.use ?? true))
        .map(chip => String(chip.title ?? ''))
        .join(' ')
        .trim()
}

const _scalar = (v) => (typeof v === 'string' || typeof v === 'number') && String(v).trim() !== ''

// webscm 2026 10 25 / ODOO-4334 (форма записи): пустая ссылка 1С — нулевой GUID (так её пишет 1С); у Odoo — false / [] / null
const REFS_ZERO_GUID_RE = /^0{8}-0{4}-0{4}-0{4}-0{12}$/

// id значения поля строкой ('' — пусто): у ссылки Odoo [id, name] — элемент [0], у составного значения — его value / id
function _refId(v) {
    if (Array.isArray(v)) v = v[0]
    if (v && typeof v === 'object') v = v.value ?? v.id
    const id = _scalar(v) ? String(v).trim() : ''
    return REFS_ZERO_GUID_RE.test(id) ? '' : id
}

// webscm 2026 10 25 / ODOO-4334 (форма записи): Shift+F3 по значению поля в ФОРМЕ записи. Вкладка формы — тоже грид, но «поле →
// значение» (main-AttrTabGrid, AttrTabGrid-INFO): строка — ПОЛЕ записи, а колонка значения одна на все поля — AttrMulti без модели. Поэтому
// тип и модель берутся из строки: field — её описание (store.prepare_AttrMulti: attr_type, relation_segment, binding, value — у ссылки
// Odoo уже id, у составного реквизита — id значения; valueStr — имя, оно и видно в ячейке и уходило в поиск до этой правки).
//   { id, segment } — искать; segment '' — без модели: GUID уникален сам по себе (так же и составной реквизит 1С — модели у него нет)
//   { id: '' }      — поле-ссылка пустое: искать нечего (App.vue не шлёт запрос и не открывает панель)
//   null            — не ссылка (обычное поле, заголовок группы): ищется значение ячейки, как раньше
// Строка «ID» (binding 'id', вкладка INFO) — своя запись, модель — сегмент записи: getRecord() → { id, segment } записи формы,
// только если значение строки и есть её id (как у ячейки id своей строки грида)
export function refs_formFieldRef(field, getRecord) {
    if (!field || typeof field !== 'object') return null
    const id = _refId(field.value)
    if (refs_isGuid(id)) return { id, segment: '' }
    if (field.binding === 'id') {
        const own = typeof getRecord === 'function' ? getRecord() : null
        return (id && own && _scalar(own.id) && String(own.id).trim() === id && typeof own.segment === 'string' && own.segment)
            ? { id, segment: own.segment } : null
    }
    const rel = typeof field.relation_segment === 'string' ? field.relation_segment.trim() : ''
    if (rel && (field.attr_type === 'AttrLinkOdoo' || field.attr_type === 'AttrLink')) return id ? { id, segment: rel } : { id: '' }
    if ((field.attr_type === 'AttrMulti' || field._valueMulti) && !id) return { id: '' }
    return null
}

// Shift+F3 по ячейке грида: на ЧТО ссылается ячейка — { id, segment } или null (тогда ищется сырое значение ячейки, как раньше).
//   attr       — описание колонки (AttrGrid.attr_by_binding[col.binding]); colBinding — binding колонки грида
//   raw        — grid.getCellData(row, col, false); dataItem — строка; getValue(item, binding) — store.get_value
//   getRecord  — () => { id, segment } записи, чья это форма (п. 3)
// 1. Ссылка Odoo (AttrLinkOdoo): колонка показывает имя (binding '<поле>[1]'), значение — [id, name]; граф знает id — элемент [0].
//    Ссылка 1С / платформы (AttrLink): значение ячейки и есть id (GUID). Модель — relation_segment колонки.
// 2. Своя запись: ячейка id строки, у которой есть свой сегмент, — «кто ссылается на ЭТУ запись», модель — сегмент записи.
// 3. Форма записи: колонка значения (binding 'attr') — тип и модель в строке (dataItem.attr), см. refs_formFieldRef; может вернуть { id: '' }
//    Соседняя колонка «Raw value» (binding 'attr.value', шаблон F_value — AttrStr без модели) показывает то же значение поля сырым — тот же путь
export function refs_cellRef({ attr, colBinding, raw, dataItem, getValue, getRecord } = {}) {
    const rel = attr && typeof attr.relation_segment === 'string' ? attr.relation_segment.trim() : ''
    if (attr && rel && (attr.attr_type === 'AttrLinkOdoo' || attr.attr_type === 'AttrLink')) {
        let v = raw
        if (attr.attr_type === 'AttrLinkOdoo' && dataItem && typeof getValue === 'function' && attr.binding) v = getValue(dataItem, attr.binding)
        if (Array.isArray(v)) v = v[0]
        return _scalar(v) ? { id: String(v).trim(), segment: rel } : null
    }
    const own = dataItem && typeof dataItem === 'object' ? dataItem : null
    const leaf = String(colBinding || '').split('.').pop()
    if (own && leaf === 'id' && _scalar(own.id) && typeof own.segment === 'string' && own.segment && String(raw ?? '') === String(own.id)) {
        return { id: String(own.id).trim(), segment: own.segment }
    }
    if (attr && (attr.binding === 'attr' || attr.binding === 'attr.value') && own && own.attr && typeof own.attr === 'object') return refs_formFieldRef(own.attr, getRecord)   // webscm 2026 10 25 / ODOO-4334 (форма записи; 'attr.value' — колонка «Raw value»)
    return null
}

// Ответ globalRefsTargets → что делать дальше:
//   timed_out — бэкенд не успел посчитать модели (statement_timeout, ответ timed_out с ПУСТЫМ targets — частичного списка #466 не
//               шлёт): панель ПРОСИТ модель и по всем моделям НЕ ищет — это та смесь «товар #6 + склад #6», которую владелец отверг.
//               Проверяется первым: пустой targets дал бы search ниже — поиск по всем моделям. Непустой targets при timed_out — защита:
//               и тогда не scope (частичный список из одной модели — не вся правда)
//   fallback — бэкенд без метода (старый стенд: диспетчер на незнакомый метод отвечает пустым ответом) — искать как раньше
//   search   — спрашивать нечего (GUID, ссылок нет, граф не построен — причину скажет обычный поиск)
//   scope    — модель одна: искать сразу в ней
//   pick     — моделей несколько: панель спрашивает «на какую?»
export function refs_targetsDecision(answer) {
    if (answer && answer.timed_out) return { action: 'timed_out' }   // webscm 2026 10 25 / ODOO-4334 (шаг 1): ответ бэкенда #466 на таймаут
    if (!answer || !Array.isArray(answer.targets)) return { action: 'fallback' }
    if (answer.is_guid || answer.graph_ready === false || !answer.targets.length) return { action: 'search' }
    if (answer.targets.length === 1 && !answer.count_unmapped) return { action: 'scope', target: answer.targets[0] }
    return { action: 'pick' }
}

// Параметры вкладки ссылок к globalRefs / globalRefsIds: счёт без задвоения слоёв (dedup) и модель цели, если она выбрана ДЛЯ ЭТОГО id
// (сменили id в чипсах — модель прежнего id не переносится). Старый бэкенд эти параметры не читает.
export function refs_requestParams(scope, id) {
    const params = { dedup: true }
    if (scope && scope.segment && scope.id === id) params.target_segment = scope.segment
    return params
}
